import { z } from 'zod';
import { projectStatusEnum } from './projects';

/**
 * Schemas for the read-only `/analytics/*` aggregation endpoints.
 *
 * Non-financial endpoints (summary counts, projects-over-time) are available
 * to any authenticated user. Revenue numbers (revenue-by-month, top-clients,
 * and the `revenue` block on the summary) are gated by
 * `PROJECTS_READ_FINANCIALS`.
 */

export const projectsByStatus = z.object({
  IN_PROGRESS: z.number().int().nonnegative(),
  ON_HOLD: z.number().int().nonnegative(),
  SENT_TO_CLIENT: z.number().int().nonnegative(),
  REOFFER: z.number().int().nonnegative(),
  ACCEPTED: z.number().int().nonnegative(),
  IN_WARRANTY: z.number().int().nonnegative(),
  ARCHIVED: z.number().int().nonnegative(),
  REJECTED: z.number().int().nonnegative(),
});
export type ProjectsByStatus = z.infer<typeof projectsByStatus>;

export const analyticsRevenue = z.object({
  grossYTD: z.number().nonnegative(),
  grossLast30Days: z.number().nonnegative(),
  pipelineGross: z.number().nonnegative(),
});
export type AnalyticsRevenue = z.infer<typeof analyticsRevenue>;

export const analyticsOperationalMetrics = z.object({
  averageResponseDays: z.number().nonnegative().nullable(),
  reofferProjects: z.number().int().nonnegative(),
});
export type AnalyticsOperationalMetrics = z.infer<typeof analyticsOperationalMetrics>;

export const analyticsSummary = z.object({
  projectsByStatus,
  totalProjects: z.number().int().nonnegative(),
  totalClients: z.number().int().nonnegative(),
  totalActiveUsers: z.number().int().nonnegative(),
  revenue: analyticsRevenue.nullable(),
  operational: analyticsOperationalMetrics,
});
export type AnalyticsSummary = z.infer<typeof analyticsSummary>;

export const projectsOverTimeBucket = z.object({
  label: z.string(), // YYYY-MM
  quote: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
  inProduction: z.number().int().nonnegative(),
  delivered: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
});
export type ProjectsOverTimeBucket = z.infer<typeof projectsOverTimeBucket>;

export const projectsOverTimeResponse = z.object({
  buckets: z.array(projectsOverTimeBucket),
});
export type ProjectsOverTimeResponse = z.infer<typeof projectsOverTimeResponse>;

export const projectsOverTimeQuery = z.object({
  months: z.coerce.number().int().min(1).max(36).default(12),
});
export type ProjectsOverTimeQuery = z.infer<typeof projectsOverTimeQuery>;

export const revenueByMonthBucket = z.object({
  label: z.string(),
  grossDelivered: z.number().nonnegative(),
});
export type RevenueByMonthBucket = z.infer<typeof revenueByMonthBucket>;

export const revenueByMonthResponse = z.object({
  buckets: z.array(revenueByMonthBucket),
});
export type RevenueByMonthResponse = z.infer<typeof revenueByMonthResponse>;

export const revenueByMonthQuery = z.object({
  months: z.coerce.number().int().min(1).max(36).default(12),
});
export type RevenueByMonthQuery = z.infer<typeof revenueByMonthQuery>;

export const topClientItem = z.object({
  id: z.string(),
  name: z.string(),
  projectCount: z.number().int().nonnegative(),
  grossTotal: z.number().nonnegative(),
  status: z.enum(['ACTIVE', 'FINALIZED']),
});
export type TopClientItem = z.infer<typeof topClientItem>;

export const topClientsResponse = z.object({
  clients: z.array(topClientItem),
});
export type TopClientsResponse = z.infer<typeof topClientsResponse>;

export const topClientsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type TopClientsQuery = z.infer<typeof topClientsQuery>;

export const projectsByEmployeeQuery = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type ProjectsByEmployeeQuery = z.infer<typeof projectsByEmployeeQuery>;

export const projectsByEmployeeItem = z.object({
  userId: z.string(),
  name: z.string(),
  accepted: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
});
export type ProjectsByEmployeeItem = z.infer<typeof projectsByEmployeeItem>;

export const projectsByEmployeeResponse = z.object({
  employees: z.array(projectsByEmployeeItem),
});
export type ProjectsByEmployeeResponse = z.infer<typeof projectsByEmployeeResponse>;

export const productCategoryKey = z.enum([
  'CABINETS',
  'PANELS',
  'COUNTERTOPS',
  'ELEMENTS',
  'LIGHTING',
  'OTHER',
]);
export type ProductCategoryKey = z.infer<typeof productCategoryKey>;

export const productCategoryAnalyticsItem = z.object({
  key: productCategoryKey,
  quantity: z.number().nonnegative(),
  percentage: z.number().min(0).max(100),
});
export type ProductCategoryAnalyticsItem = z.infer<typeof productCategoryAnalyticsItem>;

export const productCategoriesResponse = z.object({
  totalQuantity: z.number().nonnegative(),
  categories: z.array(productCategoryAnalyticsItem),
});
export type ProductCategoriesResponse = z.infer<typeof productCategoriesResponse>;

// Re-export for ProjectStatus union usage downstream.
export { projectStatusEnum };
